import { Outlet, Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppModeStore } from '@/stores/app-mode-store';
import { useInventoryStore } from '@/stores/inventory-store';
import { useYearWheelStore } from '@/stores/yearwheel-store';
import { useWaterStore } from '@/stores/water-store';
import { useBackButton } from '@/hooks/useBackButton';
import {
  Home,
  CheckSquare,
  Package,
  Droplets,
  BookOpen,
  AlertTriangle,
  Settings,
  LogOut,
  Menu,
  X,
  Target,
  ChevronRight,
  Users,
  MessageSquare,
  Heart,
  CalendarDays,
  ShoppingCart,
  PanelLeftClose,
  PanelLeftOpen,
  FileText,
  Info,
  HelpCircle,
  Search,
  Plus,
  ChefHat,
} from 'lucide-react';
import FloatingFamilyChatButton from '@/components/FloatingFamilyChatButton';
import FamilyChatPage from '@/pages/FamilyChatPage';
import LogoutConfirmModal from '@/components/LogoutConfirmModal';
import AboutModal from '@/components/AboutModal';
import { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { apiGet, getUnreadCounts } from '@/services/api-client';

// Hemberedskap-navigation
const homeNavItems = [
  { path: '/app', icon: Home, labelKey: 'nav.dashboard', exact: true },
  { path: '/app/inventory', icon: Package, labelKey: 'nav.inventory' },
  { path: '/app/water', icon: Droplets, labelKey: 'nav.water', indent: true },
  { path: '/app/shopping-list', icon: ShoppingCart, labelKey: 'nav.shoppingList', indent: true },
  { path: '/app/recipes', icon: ChefHat, labelKey: 'nav.recipes', indent: true },
  { path: '/app/checklists', icon: CheckSquare, labelKey: 'nav.checklists' },
  { path: '/app/yearwheel', icon: CalendarDays, labelKey: 'nav.yearwheel' },
  { path: '/app/notes', icon: FileText, labelKey: 'nav.notes' },
  { path: '/app/knowledge', icon: BookOpen, labelKey: 'nav.knowledge' },
  { path: '/app/vma', icon: AlertTriangle, labelKey: 'nav.vma' },
  { path: '/app/family', icon: Heart, labelKey: 'nav.family' },
  { path: '/app/settings', icon: Settings, labelKey: 'nav.settings' },
  { path: 'https://blogg.beredskapshubben.se/', icon: HelpCircle, labelKey: 'nav.help', external: true },
  { path: '/app/support', icon: MessageSquare, labelKey: 'nav.support' },
  { path: '/app/settings#about', icon: Info, labelKey: 'nav.about' },
];

// Gruppberedskap-navigation
const groupNavItems = [
  { path: '/app/groups', icon: Users, labelKey: 'nav.groups', exact: true },
  { path: '/app/groups/search', icon: Search, labelKey: 'nav.search', indent: true },
  { path: '/app/groups?create=true', icon: Plus, labelKey: 'nav.create', indent: true },
  { path: '/app/groups/notifications', icon: MessageSquare, labelKey: 'nav.notifications' },
  { path: '/app/settings', icon: Settings, labelKey: 'nav.settings' },
  { path: 'https://blogg.beredskapshubben.se/', icon: HelpCircle, labelKey: 'nav.help', external: true },
  { path: '/app/settings#about', icon: Info, labelKey: 'nav.about' },
];

// Mobilnavigeringen för Hemberedskap
const homeMobileNavItems = [
  { path: '/app', icon: Home, labelKey: 'nav.dashboard', exact: true },
  { path: '/app/inventory', icon: Package, labelKey: 'nav.inventory' },
  { path: '/app/checklists', icon: CheckSquare, labelKey: 'nav.checklists' },
  { path: '/app/yearwheel', icon: CalendarDays, labelKey: 'nav.yearwheel' },
  { path: '/app/knowledge', icon: BookOpen, labelKey: 'nav.knowledge' },
];

// Mobilnavigeringen för Gruppberedskap
const groupMobileNavItems = [
  { path: '/app/groups', icon: Users, labelKey: 'nav.groups', exact: true },
  { path: '/app/groups/search', icon: Search, labelKey: 'nav.search', exact: false },
  { path: '/app/groups?create=true', icon: Plus, labelKey: 'nav.create', exact: false },
  { path: '/app/groups/notifications', icon: MessageSquare, labelKey: 'nav.notifications', exact: false },
];

// Hjälpfunktion för att avgöra vilket läge en sökväg tillhör
const getPathMode = (path: string): 'home' | 'group' | 'shared' => {
  if (path.startsWith('/app/groups')) return 'group';
  if (path === '/app/settings') return 'shared'; // Inställningar finns i båda lägena
  return 'home';
};

interface GroupNavData {
  id: string;
  name: string;
  imageUrl?: string;
  pendingRequestCount?: number;
  unreadMessages?: number;
  lastActivityAt?: string;
}

export default function Layout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const { prepGoal, vmaRegion } = useSettingsStore();
  const { mode: appMode, setMode: setAppMode, lastHomePath, lastGroupPath, setLastPath } = useAppModeStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem('desktopSidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });

  // Disable browser's automatic scroll restoration - we manage it ourselves
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Handle back button navigation
  useBackButton();

  // Lyssna på globala event för att explicit sätta desktop-sidomenyns läge
  useEffect(() => {
    const handler = (event: Event) => {
      const desired = (event as CustomEvent<boolean>).detail;
      if (typeof desired !== 'boolean') return;
      setDesktopSidebarCollapsed(desired);
      try {
        sessionStorage.setItem('desktopSidebarCollapsed', String(desired));
        // Håll andra komponenter (t.ex. Dashboard) i synk
        window.dispatchEvent(new Event('sidebar-toggle'));
      } catch {
        // ignore storage/dispatch errors
      }
    };

    window.addEventListener('desktop-sidebar-set', handler as EventListener);
    return () => {
      window.removeEventListener('desktop-sidebar-set', handler as EventListener);
    };
  }, []);

  // Lås body scroll när mobilmenyn är öppen
  useLayoutEffect(() => {
    if (mobileMenuOpen) {
      const y = window.scrollY || window.pageYOffset || 0;
      try { document.body.dataset.prevScrollY = String(y); } catch (_e) { void _e; }
      try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
      try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* noop */ } }); } catch { /* noop */ }
    } else {
      try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
    }
    // Notify other components that the mobile menu state changed
    try {
      window.dispatchEvent(new CustomEvent('mobile-menu', { detail: mobileMenuOpen }));
      sessionStorage.setItem('mobileMenuOpen', mobileMenuOpen ? 'true' : 'false');
    } catch {
      // ignore storage / dispatch errors
    }

    return () => {
      try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
    };
  }, [mobileMenuOpen]);

  // Hjälpfunktion för att läsa testdata-toggle
  const getTestDataSetting = () => {
    const appEnv = import.meta.env.VITE_APP_ENV || 'development';
    const isNonProd = !import.meta.env.PROD && (import.meta.env.DEV || appEnv === 'staging' || appEnv === 'development');
    if (!isNonProd) return false;
    const saved = localStorage.getItem('vma-use-test-data');
    return saved !== null ? saved === 'true' : true; // Default true i dev/staging
  };

  // Reaktiv state för testdata-toggle
  const [useTestData, setUseTestData] = useState(getTestDataSetting);

  // Lyssna på storage events för att uppdatera när VmaPage togglar
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'vma-use-test-data') {
        setUseTestData(getTestDataSetting());
      }
    };
    
    // Lyssna på custom event för samma-flik uppdateringar
    const handleVmaToggle = () => {
      setUseTestData(getTestDataSetting());
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('vma-test-toggle', handleVmaToggle);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('vma-test-toggle', handleVmaToggle);
    };
  }, []);

  // Pending invites count (for badge)
  const { data: invitesSummary } = useQuery<{ inviteePending: number; ownerPending: number }>({
    queryKey: ['invites-summary', user?.id],
    queryFn: async () => {
      if (!user) return { inviteePending: 0, ownerPending: 0 };
      try {
        const res = await apiGet<{ inviteePending: number; ownerPending: number }>('/invites/summary');
        return res.success && res.data ? (res.data as { inviteePending: number; ownerPending: number }) : { inviteePending: 0, ownerPending: 0 };
      } catch (e) {
        void e;
        return { inviteePending: 0, ownerPending: 0 };
      }
    },
    refetchInterval: 60 * 1000,
    enabled: !!user,
  });

  const { data: supportSummary } = useQuery<{ unreadCount: number }>({
    queryKey: ['support-summary', user?.id],
    queryFn: async () => {
      if (!user) return { unreadCount: 0 };
      try {
        const res = await apiGet<{ summary?: { unreadCount?: number } }>('/support/cases');
        return {
          unreadCount: Number(res.success && res.data?.summary?.unreadCount ? res.data.summary.unreadCount : 0),
        };
      } catch {
        return { unreadCount: 0 };
      }
    },
    refetchInterval: 60 * 1000,
    enabled: !!user,
  });

  // Group badge count (unread messages + pending requests)
  const { data: groupsData } = useQuery<GroupNavData[]>({
    queryKey: ['groups-badge-count', user?.id],
    queryFn: async () => {
      if (!user) return [];
      try {
        const res = await apiGet<Array<{ id: string; name?: string; ownerId?: string; members?: unknown[]; unreadMessages?: number; pendingRequestCount?: number; image?: { url?: string } }>>('/groups');
        if (!res.success || !res.data) return [];
        
        // Fetch pending request counts for owned groups
        const groupsWithCounts = await Promise.all(res.data.map(async (g) => {
          const gg = g as unknown as Record<string, unknown>;
          const ownerId = typeof gg.ownerId === 'string' ? gg.ownerId : undefined;
          const groupId = String(gg.id || '');
          const name = typeof gg.name === 'string' ? gg.name : '';
          const image = gg.image && typeof gg.image === 'object' ? gg.image as Record<string, unknown> : undefined;
          const lastActivityAt = typeof gg.lastActivityAt === 'string'
            ? gg.lastActivityAt
            : (typeof gg.updatedAt === 'string' ? gg.updatedAt : undefined);
          const isOwner = ownerId && user.id === ownerId;
          
          let pendingRequestCount = 0;
          if (isOwner && groupId) {
            try {
              const reqRes = await apiGet<{ requests: unknown[] }>(`/groups/${groupId}/membership-requests`);
              if (reqRes.success && reqRes.data && reqRes.data.requests) {
                pendingRequestCount = reqRes.data.requests.length;
              }
            } catch (e) {
              // Silent fail
            }
          }
          
          return {
            id: groupId,
            name,
            imageUrl: image && typeof image.url === 'string' ? image.url : undefined,
            unreadMessages: 0, // TODO: Implement when unread messages are tracked
            pendingRequestCount,
            lastActivityAt,
          };
        }));
        
        return groupsWithCounts;
      } catch (e) {
        void e;
        return [];
      }
    },
    refetchInterval: 60 * 1000,
    enabled: !!user,
  });
  
  // Unread approved membership requests
  const { data: approvedRequestsCount } = useQuery<number>({
    queryKey: ['approved-requests-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      try {
        const res = await apiGet<{ requests: Array<{ id: string; status: string; resolvedViaInvitation?: boolean; viewedByUserAt?: string }> }>('/membership-requests/my-requests');
        if (!res.success || !res.data) return 0;
        
        // Only count approved/rejected requests that were NOT resolved via invitation and haven't been viewed
        const unviewedRequests = res.data.requests.filter(r => 
          (r.status === 'approved' || r.status === 'rejected') && 
          !r.resolvedViaInvitation && 
          !r.viewedByUserAt
        );
        
        return unviewedRequests.length;
      } catch (e) {
        void e;
        return 0;
      }
    },
    refetchInterval: 60 * 1000,
    enabled: !!user,
  });
  
  // Unread thread counts
  const { data: unreadCounts } = useQuery<Record<string, { unreadThreads: number; newBoardNotes: number; newCalendarEvents: number; newWikiArticles: number }>>({
    queryKey: ['groups-unread-counts', user?.id],
    queryFn: async () => {
      if (!user) return {};
      try {
        const res = await getUnreadCounts();
        return res.success && res.data ? res.data : {};
      } catch (e) {
        void e;
        return {};
      }
    },
    refetchInterval: 60 * 1000,
    enabled: !!user,
  });
  
  // Calculate total pending membership requests across all owned groups
  const totalPendingRequests = (groupsData || []).reduce((sum, g) => sum + (g.pendingRequestCount || 0), 0);
  
  // Notifications badge: invitations received + membership requests for owned groups + unread approved requests
  const notificationsBadgeCount = (invitesSummary?.inviteePending || 0) + totalPendingRequests + (approvedRequestsCount || 0);
  
  // Badge count for "Grupper" button (without invites)
  const groupsOnlyBadgeCount = (groupsData || []).reduce((sum, g) => sum + (g.unreadMessages || 0) + (g.pendingRequestCount || 0), 0) 
    + Object.values(unreadCounts || {}).reduce((sum, counts) => sum + Number(counts.unreadThreads || 0) + Number(counts.newBoardNotes || 0) + Number(counts.newCalendarEvents || 0) + Number(counts.newWikiArticles || 0), 0);

  const groupShortcuts = useMemo(() => {
    return (groupsData || [])
      .map((group) => {
        const counts = unreadCounts?.[group.id];
        const badgeCount = Number(counts?.unreadThreads || 0)
          + Number(counts?.newBoardNotes || 0)
          + Number(counts?.newCalendarEvents || 0)
          + Number(counts?.newWikiArticles || 0)
          + Number(group.pendingRequestCount || 0)
          + Number(group.unreadMessages || 0);

        return {
          ...group,
          badgeCount,
        };
      })
      .sort((a, b) => {
        const aTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
        const bTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
        if (aTime !== bTime) {
          return bTime - aTime;
        }

        return a.name.localeCompare(b.name, 'sv');
      });
  }, [groupsData, unreadCounts]);

  const renderGroupShortcutLink = (group: GroupNavData & { badgeCount: number }, compact = false, onClick?: () => void) => (
    <Link
      key={group.id}
      to={`/app/groups/${group.id}`}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg transition-colors ${compact ? 'px-4 py-2.5' : 'px-3 py-2 text-sm'} ${
        location.pathname === `/app/groups/${group.id}`
          ? 'bg-primary/15 text-primary-foreground'
          : 'hover:bg-muted'
      }`}
      title={group.name}
    >
      <div className={`shrink-0 overflow-hidden rounded-full border border-orange-500/50 bg-orange-500/10 flex items-center justify-center ${compact ? 'h-8 w-8' : 'h-7 w-7'}`}>
        {group.imageUrl ? (
          <img src={group.imageUrl} alt="Gruppbild" className="h-full w-full object-cover" />
        ) : (
          <Users size={compact ? 16 : 14} className="text-orange-500" />
        )}
      </div>
      <span className="min-w-0 flex-1 truncate">{group.name}</span>
      {group.badgeCount > 0 && (
        <span className={`inline-flex items-center justify-center rounded-full text-white font-medium ${compact ? 'min-w-5 px-1.5 py-0.5 text-[10px]' : 'min-w-5 px-1.5 py-0.5 text-[10px]'}`} style={{ backgroundColor: '#945123' }}>
          {group.badgeCount}
        </span>
      )}
    </Link>
  );
  
  // Total badge count for "Gruppberedskap" mode switcher (includes invites and approved requests)
  const totalGroupBadgeCount = groupsOnlyBadgeCount + (invitesSummary?.inviteePending || 0) + (approvedRequestsCount || 0);

  // Hemberedskap badge counts
  const inventoryStats = useInventoryStore((s) => s.stats);
  const yearWheelTasks = useYearWheelStore((s) => s.tasks);
  const waterContainers = useWaterStore((s) => s.containers);
  const inventoryBadgeCount = inventoryStats.expiredCount + inventoryStats.expiringCount + inventoryStats.uninventoriedCount;
  const yearWheelToday = new Date();
  yearWheelToday.setHours(0, 0, 0, 0);
  const yearWheelBadgeCount = yearWheelTasks.filter(t => {
    if (!t.enabled) return false;
    const dueDate = new Date(t.nextDue);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < yearWheelToday;
  }).length;
  const waterBadgeCount = (() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return waterContainers.filter(c => !c.filledDate || new Date(c.filledDate) <= sixMonthsAgo).length;
  })();
  const supportBadgeCount = Number(supportSummary?.unreadCount || 0);
  const totalHomeBadgeCount = inventoryBadgeCount + yearWheelBadgeCount + waterBadgeCount + supportBadgeCount;
  
  const [prevPendingCount, setPrevPendingCount] = useState<number>(notificationsBadgeCount);
  const [prevGroupBadgeCount, setPrevGroupBadgeCount] = useState<number>(totalGroupBadgeCount);
  const [prevGroupsOnlyCount, setPrevGroupsOnlyCount] = useState<number>(groupsOnlyBadgeCount);
  const [animateBadge, setAnimateBadge] = useState(false);
  const [animateGroupBadge, setAnimateGroupBadge] = useState(false);
  const [animateGroupsOnlyBadge, setAnimateGroupsOnlyBadge] = useState(false);
  const [prevSupportBadgeCount, setPrevSupportBadgeCount] = useState<number>(supportBadgeCount);
  const [animateSupportBadge, setAnimateSupportBadge] = useState(false);

  useEffect(() => {
    if (notificationsBadgeCount !== prevPendingCount) {
      setAnimateBadge(true);
      setPrevPendingCount(notificationsBadgeCount);
      const t = setTimeout(() => setAnimateBadge(false), 600);
      return () => clearTimeout(t);
    }
  }, [notificationsBadgeCount, prevPendingCount]);

  useEffect(() => {
    if (totalGroupBadgeCount !== prevGroupBadgeCount) {
      setAnimateGroupBadge(true);
      setPrevGroupBadgeCount(totalGroupBadgeCount);
      const t = setTimeout(() => setAnimateGroupBadge(false), 600);
      return () => clearTimeout(t);
    }
  }, [totalGroupBadgeCount, prevGroupBadgeCount]);

  useEffect(() => {
    if (groupsOnlyBadgeCount !== prevGroupsOnlyCount) {
      setAnimateGroupsOnlyBadge(true);
      setPrevGroupsOnlyCount(groupsOnlyBadgeCount);
      const t = setTimeout(() => setAnimateGroupsOnlyBadge(false), 600);
      return () => clearTimeout(t);
    }
  }, [groupsOnlyBadgeCount, prevGroupsOnlyCount]);

  useEffect(() => {
    if (supportBadgeCount !== prevSupportBadgeCount) {
      setAnimateSupportBadge(true);
      setPrevSupportBadgeCount(supportBadgeCount);
      const t = setTimeout(() => setAnimateSupportBadge(false), 600);
      return () => clearTimeout(t);
    }
  }, [supportBadgeCount, prevSupportBadgeCount]);

  // Refetch VMA när man navigerar till dashboard eller VMA-sidan
  useEffect(() => {
    if (location.pathname === '/app' || location.pathname === '/app/vma') {
      // Uppdatera testdata-setting och invalidera query
      setUseTestData(getTestDataSetting());
      queryClient.invalidateQueries({ queryKey: ['vma-alerts-header'] });
    }
  }, [location.pathname, queryClient]);

  // Hämta VMA-status för indikatorn
  const { data: vmaData } = useQuery<{ alerts: { id: string }[] }>(
    {
      queryKey: ['vma-alerts-header', vmaRegion, useTestData],
      queryFn: async () => {
        const base = useTestData ? '/vma/alerts?test=true' : '/vma/alerts';
        const sep = base.includes('?') ? '&' : '?';
        const countyCode = vmaRegion ? (await import('@/data/regions')).getCountyCodeForRegion(vmaRegion) : undefined;
        const regionPart = countyCode ? `${sep}counties=${encodeURIComponent(countyCode)}` : (vmaRegion ? `${sep}region=${encodeURIComponent(vmaRegion)}` : '');
        const endpoint = `${base}${regionPart}`;
        const { accessToken } = useAuthStore.getState();

        const url = `${import.meta.env.VITE_API_URL || 'http://localhost:7071/api'}${endpoint}`;
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        
        if (!res.ok) return { alerts: [] };
        const json = await res.json();
        return json.data || { alerts: [] };
      },
      refetchInterval: 5 * 60 * 1000, // Uppdatera var 5:e minut
      staleTime: 30 * 1000, // Anses färsk i 30 sekunder
      enabled: true, // Alltid köra queryn
      refetchOnMount: true, // Uppdatera när komponenten monteras
      refetchOnWindowFocus: true, // Uppdatera när fönstret får fokus
    }
  );
  const hasActiveVma = vmaData?.alerts && vmaData.alerts.length > 0;

  // Välj navigation baserat på appläge
  const navItems = appMode === 'home' ? homeNavItems : groupNavItems;
  const mobileNavItems = appMode === 'home' ? homeMobileNavItems : groupMobileNavItems;

  // Spara senaste sökväg för aktuellt läge när sökvägen ändras
  useEffect(() => {
    const pathMode = getPathMode(location.pathname);
    if (pathMode !== 'shared') {
      setLastPath(pathMode, location.pathname);
    }
  }, [location.pathname, setLastPath]);

  // Synka appMode automatiskt med nuvarande sökväg (viktigt när man navigerar från notifieringar)
  useEffect(() => {
    const pathMode = getPathMode(location.pathname);
    // Uppdatera appMode om sökvägen indikerar ett annat läge
    if (pathMode !== 'shared' && pathMode !== appMode) {
      setAppMode(pathMode);
    }
  }, [location.pathname, appMode, setAppMode]);

  // Preserve per-route scroll positions so each page scrolls independently
  // Initialize to '' so the first render is treated as a fresh navigation.
  const prevPathRef = useRef<string>('');

  useLayoutEffect(() => {
    const prev = prevPathRef.current;
    const preserveScrollY = window.__preservedScrollY;

    // If only location.state changed (path unchanged), do not alter scroll.
    // This prevents e.g. window.history.replaceState({}) calls inside pages
    // (used to clear navigation state after reading it) from triggering an
    // unwanted scroll-to-top.
    if (prev === location.pathname) {
      return;
    }
    
    // Immediately restore scroll BEFORE any rendering if we have a preserved position
    if (preserveScrollY !== undefined && location.pathname === '/app/knowledge' && prev === '/app') {
      window.scrollTo(0, preserveScrollY);
    }
    
    // Save scroll position for previous path
    if (prev && prev !== location.pathname) {
      try {
        const existing = sessionStorage.getItem(`scroll:${prev}`);
        if (!existing) {
          sessionStorage.setItem(`scroll:${prev}`, String(window.scrollY || 0));
        }
      } catch {
        // ignore storage errors
      }
    }

    // Restore scroll for the new path (if any)
    const stored = sessionStorage.getItem(`scroll:${location.pathname}`);
    
    if (location.pathname === '/app/knowledge') {
      // Don't scroll at all for knowledge page - modal takes over full screen
    } else if (location.state && (location.state as { scrollToListId?: string }).scrollToListId) {
      // Don't scroll here - let ChecklistsPage handle scrolling to the specific list
    } else if (stored) {
      window.scrollTo(0, Number(stored));
      delete window.__preservedScrollY;
    } else if (navigationType === 'POP') {
      // If the user navigated with browser back/forward (POP), do not force
      // scroll-to-top — allow the browser to restore its native scroll position.
    } else {
      // Default: scroll to top when there's no stored position and navigation
      // wasn't a POP (i.e., a normal link click or programmatic navigation).
      window.scrollTo(0, 0);
    }

    prevPathRef.current = location.pathname;
  }, [location.pathname, location.state, navigationType]);

  // Hantera lägesbyte med navigering
  const handleModeChange = (newMode: 'home' | 'group') => {
    if (newMode === appMode) return;
    
    setAppMode(newMode);
    // Navigera till senaste sökväg för det nya läget
    const targetPath = newMode === 'home' ? lastHomePath : lastGroupPath;
    navigate(targetPath);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    logout();
  };

  // Finn aktiv navitem
  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Detect when a user's `familyId` is removed (they were removed from a family).
  // Use a ref to avoid re-creating the ref on each render (prevents needing
  // to include `user?.familyId` in the effect deps and silences the linter).
  const prevFamilyRef = useRef<string | null>(user?.familyId || null);
  useEffect(() => {
    // We create an interval to check for changes in the user object which may
    // be updated by other parts of the app or via storage events.
    const check = () => {
      const current = useAuthStore.getState().user?.familyId || null;
      const prev = prevFamilyRef.current;
      if (prev && !current) {
        // Family association removed — refresh user and UI
        (async () => {
          try {
            await useAuthStore.getState().fetchUser();
          } catch (e) { void e; }
          try { queryClient.clear(); } catch (e) { void e; }
          try { navigate('/app'); } catch (e) { void e; }
        })();
      }
      prevFamilyRef.current = current;
    };

    const id = window.setInterval(check, 1500);
    // Also run once immediately
    check();
    return () => { clearInterval(id); };
  }, [queryClient, navigate]);

  // Poll for unread messages when chat is closed
  useEffect(() => {
    // Only poll if user is in a family
    const currentUser = useAuthStore.getState().user;
    if (!currentUser?.familyId) {
      setUnread(0);
      return;
    }

    let mounted = true;
    let interval: ReturnType<typeof setInterval> | undefined;
    const poll = async () => {
      try {
        const res = await apiGet<{ messages: { id: string; createdAt: string; fromId?: string; fromName?: string }[] }>('/family/messages');
        if (!mounted || !res.success || !res.data) return;
        const msgs = res.data.messages || [];
        // The server may include the user's familyLastSeen timestamp in the
        // /family/messages response meta (set by the handler). Prefer that
        // when present to avoid an extra /users/me request and ensure the
        // authoritative last-seen is used for unread calculation.
        const serverLastSeenFromMeta = (res.data && (res.data as { meta?: { familyLastSeen?: string } }).meta?.familyLastSeen) || null;
        // Exclude messages sent by the current user
        const filtered = msgs.filter(m => {
          if (!user) return true;
          if (m.fromId && user.id) return m.fromId !== user.id;
          if (m.fromName && user.displayName) return m.fromName !== user.displayName;
          return true;
        });
        // Prefer server-side lastSeen from messages meta, otherwise use
        // the local user object from auth-store or localStorage.
        let lastSeen = serverLastSeenFromMeta || null;
        if (!lastSeen && currentUser?.familyId) {
          // Use the local user object's familyLastSeen instead of fetching from server
          const familyLastSeenMap = (currentUser.settings as any)?.familyLastSeen as Record<string, string> | undefined;
          if (familyLastSeenMap && typeof familyLastSeenMap === 'object') {
            lastSeen = familyLastSeenMap[currentUser.familyId] || null;
          }
        }
        const lastSeenFinal = lastSeen || localStorage.getItem('family-chat-lastSeen');
        const count = lastSeenFinal ? filtered.filter(m => new Date(m.createdAt) > new Date(lastSeenFinal)).length : filtered.length;
        setUnread(count);
      } catch (err) {
        console.warn('Failed to poll family messages for unread count', err);
      }
    };

    if (!chatOpen) {
      poll();
      interval = setInterval(poll, 30 * 1000);
    } else {
      setUnread(0);
    }

    // Listen for realtime events from other tabs or from the open chat
    const onNew = () => {
      // If chat is closed, increment unread
      if (!chatOpen) setUnread((u) => u + 1);
    };
    window.addEventListener('family-chat:new-message', onNew as EventListener);

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'family-chat-new') {
        if (!chatOpen) setUnread((u) => u + 1);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      mounted = false;
      if (interval) clearInterval(interval);
      window.removeEventListener('family-chat:new-message', onNew as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, [chatOpen, user]);

  // Listen for notification clicks to open family chat
  useEffect(() => {
    const handleOpenChat = () => {
      console.log('[Layout] Received open-family-chat event, opening chat modal');
      setChatOpen(true);
    };
    
    console.log('[Layout] Setting up open-family-chat event listener');
    window.addEventListener('open-family-chat', handleOpenChat);
    
    // Check if we should open chat on mount (from notification click)
    const shouldOpenChat = sessionStorage.getItem('openFamilyChatOnLoad');
    if (shouldOpenChat === 'true') {
      console.log('[Layout] Found openFamilyChatOnLoad flag, opening chat');
      sessionStorage.removeItem('openFamilyChatOnLoad');
      setChatOpen(true);
    }
    
    return () => {
      console.log('[Layout] Removing open-family-chat event listener');
      window.removeEventListener('open-family-chat', handleOpenChat);
    };
  }, []);

  // Breadcrumbs
  const breadcrumbs = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const crumbs: { label: string; path: string }[] = [];
    
    if (pathParts[0] === 'app') {
      crumbs.push({ label: t('nav.dashboard'), path: '/app' });
      
      if (pathParts[1]) {
        const allNavItems = [...homeNavItems, ...groupNavItems];
        const navItem = allNavItems.find(item => item.path === `/app/${pathParts[1]}`);
        if (navItem) {
          crumbs.push({ label: t(navItem.labelKey), path: navItem.path });
        }
      }
    }
    
    return crumbs;
  }, [location.pathname, t]);

  const prepGoalLabels: Record<string, string> = {
    '7d': '7d',
    '30d': '30d',
    '90d': '90d+',
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header - fixed så den alltid syns */}
      <header className="fixed top-0 left-0 right-0 z-50 h-12 flex items-center justify-between border-b-2 border-white/20 bg-card backdrop-blur shadow-sm px-3 md:hidden">
        <button 
            onClick={() => {
            setAppMode('home');
            try { sessionStorage.removeItem('scroll:/app'); } catch { /* noop */ }
            navigate('/app');
            // ensure we are at the top
            setTimeout(() => window.scrollTo(0, 0), 0);
          }}
          className="flex items-center gap-2 text-xl font-bold text-primary"
        >
          <img src="/icons/logo.svg" alt="" className="h-7 w-auto" />
          {t('app.name')}
        </button>
        <div className="flex items-center gap-2">
          <div className="vma-button-wrap">
          <button
            onClick={() => {
              setAppMode('home');
              navigate('/app/vma');
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
              hasActiveVma 
                ? 'bg-red-500/20 hover:bg-red-500/30' 
                : 'bg-primary/10 hover:bg-primary/20'
            }`}
            title={hasActiveVma ? t('vma.hasAlerts') : t('vma.noAlerts')}
          >
            <AlertTriangle
              className={`h-4 w-4 ${hasActiveVma ? 'text-red-500 drop-shadow-[0_0_10px_rgba(239,68,68,0.85)] animate-pulse' : 'text-primary'}`}
            />
            {hasActiveVma && (
              <>
                <span className="vma-ring vma-ring--one" />
                <span className="vma-ring vma-ring--two" />
                <span className="vma-ring vma-ring--three" />
              </>
            )}
          </button>
          </div>
          <button 
            onClick={() => {
              setAppMode('home');
              navigate('/app/settings#prep-goal');
            }}
            className="flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-xs hover:bg-primary/20 transition-colors"
          >
            <Target className="h-4 w-4 text-primary" />
            <span className="text-primary font-medium">{prepGoalLabels[prepGoal]}</span>
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-md p-0.5 hover:bg-muted"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background md:hidden flex flex-col">
          {/* Spacer för header */}
          <div className="h-12 flex-shrink-0" />
          <nav className="flex-1 flex flex-col p-4 pb-24 overflow-y-auto">
            {navItems.map((item, index) => {
              // Special handling for external links
              if ('external' in item && item.external) {
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-colors relative hover:bg-muted text-left`}
                  >
                    <item.icon size={20} />
                    <span>{t(item.labelKey)}</span>
                  </a>
                );
              }
              
              // Special handling for About link
              if (item.path === '/app/settings#about') {
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      setShowAboutModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-colors relative hover:bg-muted text-left ${'indent' in item && (item as { indent?: boolean }).indent ? 'pl-6' : ''}`}
                  >
                    <item.icon size={20} />
                    <span>{t(item.labelKey)}</span>
                  </button>
                );
              }
              
              return (
                <div key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-colors relative ${
                          isActive(item.path, item.exact)
                            ? 'bg-primary/20 text-primary-foreground'
                            : 'hover:bg-muted'
                    } ${'indent' in item && (item as { indent?: boolean }).indent ? 'pl-6' : ''}`}
                  >
                    <div className={item.path === '/app/vma' && hasActiveVma ? 'vma-button-wrap' : undefined}>
                      <item.icon
                        size={20}
                        className={item.path === '/app/vma' && hasActiveVma ? 'text-red-500 drop-shadow-[0_0_10px_rgba(239,68,68,0.85)] animate-pulse' : undefined}
                      />
                      {item.path === '/app/vma' && hasActiveVma && (
                        <>
                          <span className="vma-ring vma-ring--one" />
                          <span className="vma-ring vma-ring--two" />
                        </>
                      )}
                    </div>
                    <span>{t(item.labelKey)}</span>
                    {item.path === '/app/support' && supportBadgeCount > 0 && (
                      <span className={`ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-white text-[10px] font-medium ${animateSupportBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>
                        {supportBadgeCount}
                      </span>
                    )}
                    {item.path === '/app/vma' && hasActiveVma && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    )}
                    {index === 0 && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setAppMode('home');
                          navigate('/app/settings');
                          setMobileMenuOpen(false);
                        }}
                        className="ml-auto rounded-md p-1.5 hover:bg-muted-foreground/20 transition-colors"
                        title={t('nav.settings')}
                      >
                        <Settings size={18} />
                      </button>
                    )}
                  </Link>

                  {appMode === 'group' && item.path === '/app/groups?create=true' && groupShortcuts.length > 0 && (
                    <div className="mt-1 space-y-1 pl-6">
                      {groupShortcuts.map((group) => renderGroupShortcutLink(group, true, () => setMobileMenuOpen(false)))}
                    </div>
                  )}
                </div>
              );
            })}
            <button
              onClick={handleLogout}
              className="mt-4 flex items-center gap-3 rounded-lg px-4 py-3 text-destructive hover:bg-muted"
            >
              <LogOut size={20} />
              <span>{t('nav.logout')}</span>
            </button>
          </nav>
        </div>
      )}

      {/* Floating family chat button (visible when user belongs to a family) */}
      {user?.familyId && (
        <>
          <FloatingFamilyChatButton onOpen={() => setChatOpen(true)} unread={unread} />
          {chatOpen && <FamilyChatPage onClose={() => { setChatOpen(false); try { localStorage.setItem('family-chat-lastSeen', new Date().toISOString()); } catch (e) { console.warn('Could not set family-chat-lastSeen'); void e; } }} />}
        </>
      )}

      {/* Mobile bottom navigation with mode tabs */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-white/20 bg-card md:hidden">
        {/* Mode switcher tabs */}
        <div className="flex border-b">
          <button
            onClick={() => handleModeChange('home')}
            className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-all ${
              appMode === 'home'
                ? 'text-[#b39872] border-b-2 border-[#ed8b00] ring-1 ring-[#ed8b00]/50 ring-inset'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Home size={14} />
            <span>{t('nav.homeMode')}</span>
            {totalHomeBadgeCount > 0 && (
              <span className={`absolute -top-1.5 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium ${appMode === 'home' ? 'opacity-50' : ''}`} style={{ backgroundColor: '#945123' }}>{totalHomeBadgeCount}</span>
            )}
          </button>
          <button
            onClick={() => handleModeChange('group')}
            className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-all ${
              appMode === 'group'
                ? 'text-[#b39872] border-b-2 border-[#ed8b00] ring-1 ring-[#ed8b00]/50 ring-inset'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users size={14} />
            <span>{t('nav.groupMode')}</span>
            {totalGroupBadgeCount > 0 && (
              <span className={`absolute -top-1.5 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium ${appMode === 'group' ? 'opacity-50' : ''} ${animateGroupBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{totalGroupBadgeCount}</span>
            )}
          </button>
        </div>
        {/* Navigation items */}
        {(() => {
          const visibleBottomItems = mobileNavItems.filter(i => !('indent' in i && (i as { indent?: boolean }).indent));
          return (
            <div className={`grid gap-1 px-2 py-2 ${visibleBottomItems.length <= 2 ? 'grid-cols-2' : visibleBottomItems.length <= 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
              {visibleBottomItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => {
                    if (mobileMenuOpen) {
                      setMobileMenuOpen(false);
                    }
                    // Tvinga omedelbar scroll till toppen för att undvika blinkning
                    if (!isActive(item.path, item.exact)) {
                      e.preventDefault();
                      window.scrollTo(0, 0);
                      navigate(item.path);
                    }
                  }}
                  className={`relative flex flex-col items-center justify-center py-1 rounded-lg transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'text-[#b39872] ring-1 ring-[#ed8b00]/60 shadow-[0_0_7px_rgba(237,139,0,0.4)]'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <item.icon size={20} />
                  <span className="text-[10px] mt-0.5 truncate max-w-full">
                    {t(item.labelKey).split(' ')[0]}
                  </span>
                  {item.path === '/app/groups' && groupsOnlyBadgeCount > 0 && (
                    <span className={`absolute -top-1 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium ${animateGroupsOnlyBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{groupsOnlyBadgeCount}</span>
                  )}
                  {item.path === '/app/groups/notifications' && notificationsBadgeCount > 0 && (
                    <span className={`absolute -top-1 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium ${animateBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{notificationsBadgeCount}</span>
                  )}
                  {item.path === '/app/inventory' && (inventoryBadgeCount + waterBadgeCount) > 0 && (
                    <span className="absolute -top-1 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium" style={{ backgroundColor: '#945123' }}>{inventoryBadgeCount + waterBadgeCount}</span>
                  )}
                  {item.path === '/app/yearwheel' && yearWheelBadgeCount > 0 && (
                    <span className="absolute -top-1 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium" style={{ backgroundColor: '#945123' }}>{yearWheelBadgeCount}</span>
                  )}
                  {item.path === '/app/water' && waterBadgeCount > 0 && (
                    <span className="absolute -top-1 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium" style={{ backgroundColor: '#945123' }}>{waterBadgeCount}</span>
                  )}
                </Link>
              ))}
            </div>
          );
        })()}
      </nav>

      <div className="flex">
        {/* Desktop sidebar - fixed position */}
        <aside className={`hidden shrink-0 border-r bg-card md:block fixed top-0 left-0 h-screen z-40 transition-transform duration-300 ${
          desktopSidebarCollapsed ? '-translate-x-full' : 'translate-x-0'
        } w-[340px]`}>
          <div className="flex h-full flex-col w-[340px]">
            <div className="border-b p-6">
              <button 
                onClick={() => {
                    setAppMode('home');
                    try { sessionStorage.removeItem('scroll:/app'); } catch { /* noop */ }
                    navigate('/app');
                    setTimeout(() => window.scrollTo(0, 0), 0);
                  }}
                className="flex items-center gap-2 text-2xl font-bold text-primary"
              >
                <img src="/icons/logo.svg" alt="" className="h-8 w-auto" />
                {t('app.name')}
              </button>
              <p className="mt-3 text-sm text-muted-foreground soft-wrap">
                {user?.displayName}
              </p>
              <Link 
                to="/app/settings#prep-goal" 
                className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
              >
                <Target className="h-4 w-4 text-primary" />
                <span className="text-sm">
                  <span className="text-muted-foreground">Beredskapsmål:</span>
                  <span className="ml-1 font-medium text-primary">{t(`prepLevels.${prepGoal}`)}</span>
                </span>
              </Link>
            </div>

            {/* Mode switcher - desktop */}
            <div className="p-4 border-b">
              <div className="flex rounded-lg bg-muted p-1 overflow-visible">
                <button
                  onClick={() => handleModeChange('home')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all relative z-10 ${
                    appMode === 'home'
                      ? 'bg-background text-[#b39872] shadow-sm ring-1 ring-[#ed8b00]/70 shadow-[0_0_8px_rgba(237,139,0,0.45)]'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Home size={16} />
                  <span>{t('nav.homeMode')}</span>
                  {totalHomeBadgeCount > 0 && (
                    <span className={`absolute -top-1.5 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium ${appMode === 'home' ? 'opacity-50' : ''}`} style={{ backgroundColor: '#945123' }}>{totalHomeBadgeCount}</span>
                  )}
                </button>
                <button
                  onClick={() => handleModeChange('group')}
                  className={`relative flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    appMode === 'group'
                      ? 'bg-background text-[#b39872] shadow-sm ring-1 ring-[#ed8b00]/70 shadow-[0_0_8px_rgba(237,139,0,0.45)]'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Users size={16} />
                  <span>{t('nav.groupMode')}</span>
                  {totalGroupBadgeCount > 0 && (
                    <span className={`absolute -top-1.5 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium ${appMode === 'group' ? 'opacity-50' : ''} ${animateGroupBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{totalGroupBadgeCount}</span>
                  )}
                </button>
              </div>
            </div>

            <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
              {navItems.map((item, index) => {
                // Special handling for external links
                if ('external' in item && (item as { external?: boolean }).external) {
                  return (
                    <div key={item.path}>
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full flex items-center gap-3 rounded-lg px-4 py-2.5 transition-colors relative hover:bg-muted text-left ${'indent' in item && (item as { indent?: boolean }).indent ? 'pl-8' : ''}`}
                      >
                        <item.icon size={'indent' in item && (item as { indent?: boolean }).indent ? 16 : 20} />
                        <span>{t(item.labelKey)}</span>
                      </a>
                    </div>
                  );
                }
                
                // Special handling for About link
                if (item.path === '/app/settings#about') {
                  return (
                    <div key={item.path}>
                      <button
                        onClick={() => setShowAboutModal(true)}
                        className={`w-full flex items-center gap-3 rounded-lg px-4 py-2.5 transition-colors relative hover:bg-muted text-left ${'indent' in item && (item as { indent?: boolean }).indent ? 'pl-8' : ''}`}
                      >
                        <item.icon size={'indent' in item && (item as { indent?: boolean }).indent ? 16 : 20} />
                        <span>{t(item.labelKey)}</span>
                      </button>
                    </div>
                  );
                }
                
                return (
                  <div key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 rounded-lg px-4 py-2.5 transition-colors relative ${
                        isActive(item.path, item.exact)
                              ? 'bg-primary/20 text-primary-foreground'
                              : 'hover:bg-muted'
                          } ${'indent' in item && (item as { indent?: boolean }).indent ? 'pl-8' : ''}`}
                        >
                          <div className={item.path === '/app/vma' && hasActiveVma ? 'vma-button-wrap' : undefined}>
                            <item.icon
                              size={'indent' in item && (item as { indent?: boolean }).indent ? 16 : 20}
                              className={item.path === '/app/vma' && hasActiveVma ? 'text-red-500 drop-shadow-[0_0_12px_rgba(239,68,68,0.9)] animate-pulse' : undefined}
                            />
                            {item.path === '/app/vma' && hasActiveVma && (
                              <>
                                <span className="vma-ring vma-ring--one-desktop" />
                                <span className="vma-ring vma-ring--two-desktop" />
                                <span className="vma-ring vma-ring--three-desktop" />
                              </>
                            )}
                          </div>
                      <span>{t(item.labelKey)}</span>
                      {item.path === '/app/support' && supportBadgeCount > 0 && (
                        <span className={`ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-white text-xs font-medium ${animateSupportBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{supportBadgeCount}</span>
                      )}
                      {item.path === '/app/groups' && groupsOnlyBadgeCount > 0 && (
                        <span className={`ml-auto -mr-28 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-white text-xs font-medium ${animateGroupsOnlyBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{groupsOnlyBadgeCount}</span>
                      )}
                      {item.path === '/app/groups/notifications' && notificationsBadgeCount > 0 && (
                        <span className={`ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-white text-xs font-medium ${animateBadge ? 'badge-animate' : ''}`} style={{ backgroundColor: '#945123' }}>{notificationsBadgeCount}</span>
                      )}
                      {item.path === '/app/inventory' && inventoryBadgeCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-white text-xs font-medium" style={{ backgroundColor: '#945123' }}>{inventoryBadgeCount}</span>
                      )}
                      {item.path === '/app/yearwheel' && yearWheelBadgeCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-white text-xs font-medium" style={{ backgroundColor: '#945123' }}>{yearWheelBadgeCount}</span>
                      )}
                      {item.path === '/app/water' && waterBadgeCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-white text-xs font-medium" style={{ backgroundColor: '#945123' }}>{waterBadgeCount}</span>
                      )}
                      {item.path === '/app/vma' && hasActiveVma && (
                        <span className="ml-auto w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#945123' }} />
                      )}
                      {index === 0 && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppMode('home');
                            navigate('/app/settings');
                          }}
                          className="ml-auto rounded-md p-1.5 hover:bg-muted-foreground/20 transition-colors"
                        title={t('nav.settings')}
                      >
                        <Settings size={16} />
                      </button>
                    )}
                  </Link>

                    {appMode === 'group' && item.path === '/app/groups?create=true' && groupShortcuts.length > 0 && (
                      <div className="mt-2 space-y-1 pl-8 pr-2">
                        {groupShortcuts.map((group) => renderGroupShortcutLink(group))}
                      </div>
                    )}
                </div>
                );
              })}
            </nav>

            <div className="border-t p-4">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-destructive hover:bg-muted"
              >
                <LogOut size={20} />
                <span>{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Spacer for fixed sidebar on desktop - removed as we use margin instead */}

        {/* Toggle button for desktop sidebar */}
        <button
          onClick={() => {
            const newState = !desktopSidebarCollapsed;
            setDesktopSidebarCollapsed(newState);
            sessionStorage.setItem('desktopSidebarCollapsed', String(newState));
            window.dispatchEvent(new Event('sidebar-toggle'));
          }}
          className={`hidden md:flex fixed top-1 z-50 items-center justify-center w-10 h-10 rounded-full bg-card border border-border shadow-lg hover:bg-muted transition-all duration-300 ${
            desktopSidebarCollapsed ? 'left-4' : 'left-[324px]'
          }`}
          title={desktopSidebarCollapsed ? 'Visa navigering' : 'Dölj navigering'}
        >
          {desktopSidebarCollapsed ? (
            <PanelLeftOpen className="h-5 w-5" style={{ color: '#868179' }} />
          ) : (
            <PanelLeftClose className="h-5 w-5" style={{ color: '#868179' }} />
          )}
        </button>

        {/* Main content */}
        <main className={`flex-1 pt-12 pb-28 md:pt-0 md:pb-0 transition-all duration-300 ${
          desktopSidebarCollapsed ? 'md:ml-0' : 'md:ml-[340px]'
        }`}>
          {/* Breadcrumbs - desktop only */}
          {breadcrumbs.length > 1 && (
            <div className={`hidden md:flex items-center gap-2 py-4 text-sm border-b bg-muted/30 transition-all duration-300 ${
              desktopSidebarCollapsed ? 'pl-16 pr-8' : 'px-8'
            }`}>
              {breadcrumbs.map((crumb, index) => (
                <span key={crumb.path} className="flex items-center gap-2">
                  {index > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="font-medium">{crumb.label}</span>
                  ) : (
                    <button
                      onClick={() => {
                        if (crumb.path === '/app') {
                          setAppMode('home');
                        }
                        navigate(crumb.path);
                      }}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {crumb.label}
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          <div className="p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutModal(false)}
      />

      {/* About Modal */}
      <AboutModal
        open={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />
    </div>
  );
}
